import * as stylex from '@stylexjs/stylex'
import { type ElementType, type ReactNode } from 'react'
import { Box, type BoxProps } from '../Box/Box'
import { IconButton } from '../IconButton/IconButton'
import { CloseLarge } from '../Icons'
import { Text, type TextProps } from '../Text/Text'
import { colors } from '../tokens/colors.stylex'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const _baseStyles = stylex.create({
  root: {
    padding: '16px',
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    position: 'relative',
    width: '100%',
  },
  /** 只有置中標題套用：左右對齊的標題兩側留白不對稱，補上任一側都會讓既有標題整個位移 */
  closeButtonSpace: {
    // 48 = 關閉鈕的 right: 12 加上 IconButton size="medium" 的寬度 36（icon 20 + padding 8×2）
    paddingInline: 48,
  },
  titleContainer: {
    width: '100%',
  },
  closeButton: {
    position: 'absolute',
    right: 12,
    top: 8,
  },
})

export type DialogHeaderProps<RootComponent extends ElementType = 'div', BoxElement extends HTMLElement = HTMLDivElement> = Omit<BoxProps<RootComponent, BoxElement>, 'title'> & {
  /**
   * 標題內容
   */
  title?: ReactNode
  /**
   * 標題對齊方式
   * @default 'center'
   */
  align?: TextProps['align']
  /**
   * 自訂內部子元素樣式覆寫
   */
  xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
  /**
   * 關閉按鈕點擊回呼，若未傳入則不顯示關閉按鈕
   */
  onClose?: () => void
  /**
   * 子元素，當 title 未傳入時作為標題內容
   */
  children?: ReactNode
}

/**
 * DialogHeader 對話框標題列元件。
 * 包含標題文字與可選的關閉按鈕。
 */
export const DialogHeader = <RootComponent extends ElementType = 'div', BoxElement extends HTMLElement = HTMLDivElement>(props: DialogHeaderProps<RootComponent, BoxElement>) => {
  const { component = 'div', rootStyle, xStyles, title, onClose, align = 'center', children, ...rest } = props
  const baseStyles = useXStyles(_baseStyles, xStyles)

  return (
    <Box
      component={component}
      rootStyle={[baseStyles.root, onClose && align === 'center' && baseStyles.closeButtonSpace, rootStyle]}
      {...rest}
    >
      <div {...stylex.props(baseStyles.titleContainer)}>
        <Text variant="h5" align={align}>{title ?? children}</Text>
      </div>
      {onClose
      && (
        <IconButton
          data-testid="dialog-close-button"
          rootStyle={baseStyles.closeButton}
          onClick={onClose}
          variant="circle"
        >
          <CloseLarge color={colors.grey600} />
        </IconButton>
      )}
    </Box>
  )
}
